import HttpClientInterface from "@/services/HttpClient/HttpClientInterface";
import axios from "axios";
import ClientInstance from "@/types/HttpClient/clientInstance";
import ClientResponse from "@/types/HttpClient/clientResponse";
import PostUserType from "@/types/Authentication/PostUserType";
import { SearchJobType } from "@/types/job/SearchJobType";
import { SaveJobOfferType } from "@/types/job/SaveJobOfferType";

export default class HttpClient implements HttpClientInterface {
  private readonly clientInstance: ClientInstance;

  constructor() {
    this.clientInstance = axios.create({
      baseURL: process.env.VUE_APP_JOB_BOARD_API_ENDPOINT,
    });
  }

  /**
   *
   * @param uri
   * @param token
   */
  public async fetchFromApiPlatformUri(
    uri: string,
    token: string
  ): Promise<ClientResponse> {
    return await this.clientInstance.get(uri, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   *
   * @param uri
   * @param token
   */
  public async postFromApiPlatformUri(
    uri: string,
    data: any,
    token: string
  ): Promise<ClientResponse> {
    return await this.clientInstance.post(uri, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   *
   * @param uri
   * @param token
   */
  public async putFromApiPlatformUri(
    uri: string,
    data: any,
    token: string
  ): Promise<ClientResponse> {
    return await this.clientInstance.put(uri, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * return job offers count by activity sector
   */
  public async browseCategoriesForHomepage(): Promise<ClientResponse> {
    return await this.clientInstance.get("/public/browse/homepage-categories");
  }

  /**
   * return alerts of current user
   */
  public async getCurrentUserAlerts(token: string): Promise<ClientResponse> {
    return await this.clientInstance.get("/api/candidate/alerts", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * get job offers type
   */
  public async getJobOffersType(token: string): Promise<ClientResponse> {
    return await this.clientInstance.get("/api/job_offer_types", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * get jobs
   */
  public async getJobs(token: string): Promise<ClientResponse> {
    return await this.clientInstance.get("/api/jobs", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * return applications of current user
   */
  public async getCurrentUserApplications(
    token: string
  ): Promise<ClientResponse> {
    return await this.clientInstance.get("/api/candidate/applications", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * return alerts of current user
   */
  public async getCurrentUserSavedJobOffers(
    token: string
  ): Promise<ClientResponse> {
    return await this.clientInstance.get("/api/candidate/job_offers/save", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * Get job offers, members, applications and companies count
   */
  public async getItemsCount(): Promise<ClientResponse> {
    return this.clientInstance.get("/public/item-counts");
  }

  /**
   * @inheritDoc
   */
  public async getActivitiesSector(): Promise<ClientResponse> {
    return this.clientInstance.get("/public/activities-sector");
  }

  /**
   * @inheritDoc
   */
  public async getActivitiesSectorWithOriginalApi(
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.get("/api/activity_sectors", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async getMyCompanyInformations(
    id: number,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.get("/api/companies/" + id, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async registerUser(user: PostUserType): Promise<ClientResponse> {
    return this.clientInstance.post("/custom/user/registration", user);
  }

  public async updateUserPassword(
    user: PostUserType,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post("/api/user/update-password", user, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async deleteAlert(id: number, token: string): Promise<ClientResponse> {
    return this.clientInstance.delete("/api/alerts/" + id, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async deleteSearchHistory(
    id: number,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.delete("/api/job_research_histories/" + id, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async deleteSavedOffer(
    id: number,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.delete("/api/candidate/job_offers/save/" + id, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async loginUser(user: Partial<PostUserType>): Promise<ClientResponse> {
    return this.clientInstance.post("/api/login_check", user);
  }

  public async resetPasswordForReal(user: any): Promise<ClientResponse> {
    return this.clientInstance.post("/public/update/password", user);
  }

  public async checkResetTokenIsValid(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/public/check/reset/token/" + token);
  }

  public async checkAndConfirmUser(token: string): Promise<ClientResponse> {
    return this.clientInstance.post("/public/user/confirmation/" + token);
  }

  public async updateUserInformations(
    user: Partial<PostUserType>,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.put("/api/users/" + user.id, user, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async updateSeekerInformations(
    seeker: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.put("/api/seekers/" + seeker.id, seeker, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async fetchCompanyKeyWordsJobTitleAndActivitySector(
    value: string,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post(
      "/api/candidate/search-job-word",
      {
        term: value,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  }

  public async fetchCompanyKeyWordsJobTitleAndActivitySectorCompany(
    value: string,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post(
      "/api/company/search-job-word",
      {
        term: value,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  }

  public async fetchCompanyKeyWordsJobTitleAndActivitySectorForPublicUser(
    value: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post("/public/search-job-word", {
      term: value,
    });
  }

  public async fetchLocations(
    value: string,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post(
      "/public/locations",
      {
        location: value,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  }

  public async getJobOffers(
    value: SearchJobType,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post("/api/candidate/jobs", value, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async getPublicJobOffers(
    value: SearchJobType,
    token?: string
  ): Promise<ClientResponse> {

    let url = "/public/jobs"
    if (token) {
      url = url + "?access_token=" + token;
    }

    return this.clientInstance.post(url, value);
  }

  public async resetPassword(value: any): Promise<ClientResponse> {
    console.log(value);
    return this.clientInstance.post("/public/reset-password", value);
  }

  public async getJobOffer(id: number, token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/candidate/job/" + id, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async getCurrentSeeker(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/candidate/get_current_seeker", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async createNewJobOffersAlert(
    value: SearchJobType,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post("/api/candidate/alerts", value, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async saveJobOffer(
    value: SaveJobOfferType,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post("/api/candidate/job_offers/save", value, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async getLastJobSearch(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/candidate/job_research_history", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async uploadSeekerCvFile(formData: any, token: string) {
    return this.clientInstance.post("/api/candidate/cv", formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
  }

  public async uploadSeekerAvatarFile(formData: any, token: string) {
    return this.clientInstance.post("/api/candidate/avatar", formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
  }

  public async getCvPreview(url: string, token: string) {
    return this.clientInstance.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async createNewApplication(application: any, token: string) {
    return this.clientInstance.post(
      "/api/candidate/job_offer_application/new",
      application,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public async getApplication(applicationId: number, token: string) {
    return this.clientInstance.get(
      "/api/candidate/job_offer_application/" + applicationId,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public async getMyProfileInformationsFilledPercentage(token: string) {
    return this.clientInstance.get(
      "/api/candidate/personalInformationsPercentage",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public async setMyProfileInformationsFilledPercentage(
    flag: string,
    token: string
  ) {
    return this.clientInstance.post(
      "/api/candidate/personalInformationsPercentage",
      {
        flag: flag,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public async fetchSeekersCompany(
    value: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post(
      "/api/company/seeker-cvs",
      {
        searchContent: value,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  }

  public async fetchSeekerForCvteque(
    seeker: number,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.get("/api/company/seeker-cv/" + seeker, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async updateCompanyInformations(
    company: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.put("/api/companies/" + company.id, company, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async getCompany(id: number): Promise<ClientResponse> {
    return this.clientInstance.get("/public/api/company/" + id);
  }

  public async uploadCompanyLogoAndCoverPicture(formData: any, token: string) {
    return this.clientInstance.post(
      "/api/company/logo_and_cover_picture",
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
  }

  public async getCompanyJobOffers(token: string) {
    return this.clientInstance.get("/api/company/job_offers", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async getCurrentUserInformations(
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.get("/api/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  //Experience details

  public async getExperienceDetails(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/experience_details", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async getExperienceDetailsByUser(
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.get("/api/experience_details_by_user", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async createExperienceDetails(
    experienceDetails: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post(
      "/api/experience_details",
      experienceDetails,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public async updateExperienceDetails(
    id: any,
    experienceDetails: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.put(
      "/api/experience_details/" + id,
      experienceDetails,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public async deleteExperienceDetails(
    experienceDetails: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.delete(
      "/api/experience_details/" + experienceDetails.id,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  //education degrees

  public async getEducationsDegrees(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/education_degrees", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async getEducationsDegreesByUser(
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.get("/api/education_degrees_by_user", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async createEducationDegrees(
    educationDegree: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post("/api/education_degrees", educationDegree, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async deleteEducationDegrees(
    educationDegree: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.delete(
      "/api/education_degrees/" + educationDegree.id,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public async updateEducationDegrees(
    id: any,
    educationDegree: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.put(
      "/api/education_degrees/" + id,
      educationDegree,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }
  //skill

  public async getSkills(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/skills", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  //seeker skill
  public async createSeekerSkills(
    seekerSkill: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post("/api/seeker_skills", seekerSkill, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async deleteSeekerSkills(
    seekerSkill: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.delete("/api/seeker_skills/" + seekerSkill.id, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async getSeekerSkills(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/seeker_skills", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async getSeekerSkillsByUser(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/seeker_skills_by_user", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  //center of interest

  public async getCenterOfInterst(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/center_of_intersts", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  //seeker center of interest

  public async createSeekerCenterOfIntersts(
    seekerCenterOfInterst: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post(
      "/api/seeker_center_of_interests",
      seekerCenterOfInterst,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public async deleteSeekerCenterOfIntersts(
    seekerCenterOfInterst: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.delete(
      "/api/seeker_center_of_interests/" + seekerCenterOfInterst.id,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public async getSeekerCenterOfInterst(
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.get("/api/seeker_center_of_interests", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async getSeekerCenterOfInterstByUser(
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.get("/api/seeker_center_of_interests_by_user", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  //langue

  public async getSeekerLangues(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/seeker_languages", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async getSeekerLanguesByUser(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/seeker_languages_by_user", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async getLangues(token: string): Promise<ClientResponse> {
    return this.clientInstance.get("/api/languages", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async createSeekerLangues(
    seekerLangue: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.post("/api/seeker_languages", seekerLangue, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async deleteSeekerLangues(
    seekerLangue: any,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.delete(
      "/api/seeker_languages/" + seekerLangue.id,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public async downloadResume(
    link: string,
    token: string
  ): Promise<ClientResponse> {
    return this.clientInstance.get(link, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
